import {
  ApplicationConfig,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
  importProvidersFrom,
  ErrorHandler,
} from '@angular/core';
import { environment } from '../environments/environment';
import { provideRouter } from '@angular/router';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { en_US, provideNzI18n } from 'ng-zorro-antd/i18n';
import { provideAnimations } from '@angular/platform-browser/animations';
import { routes } from './app.routes';
import { authInterceptor, errorInterceptor, retryInterceptor } from '@lp-ui-lib';
import { provideServiceWorker } from '@angular/service-worker';

import { MSAL_INSTANCE, MsalService, MsalBroadcastService } from '@azure/msal-angular';

import { PublicClientApplication, IPublicClientApplication } from '@azure/msal-browser';

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: 'dummy-client-id',
      authority: 'https://login.microsoftonline.com/common',
      redirectUri: window.location.origin,
    },
  });
}

// Custom error handler to suppress external script errors
class GlobalErrorHandler implements ErrorHandler {
  handleError(error: any): void {
    const errorMessage = error?.message || error?.toString() || '';
    const errorStack = error?.stack || '';

    // Check if it's a script error from external sources
    if (
      error?.message === 'Script error.' ||
      errorMessage.includes('Script error') ||
      (error instanceof ErrorEvent && error.message === 'Script error.')
    ) {
      console.warn('External script error suppressed:', error);
      return;
    }

    // Check if it's from OneTrust SDK
    if (
      errorStack.includes('otBannerSdk.js') ||
      errorMessage.includes('TenantFeatures') ||
      errorMessage.includes('OneTrust') ||
      errorStack.includes('cookielaw.org')
    ) {
      console.warn('OneTrust SDK error suppressed:', errorMessage);
      return;
    }

    // Check if it's from Adobe Data Layer
    if (errorStack.includes('adobeDataLayer') || errorMessage.includes('adobe')) {
      console.warn('Adobe Data Layer error suppressed:', errorMessage);
      return;
    }

    // Log other errors normally
    console.error('Application error:', error);
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    // Angular core and browser providers
    provideAnimations(),
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),

    // Router
    provideRouter(routes),

    // HTTP and forms
    importProvidersFrom(HttpClient),
    provideHttpClient(withInterceptors([authInterceptor, errorInterceptor, retryInterceptor])),

    // Third-party (ng-zorro)
    provideNzI18n(en_US),

    // Custom error handler
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    MsalService,
    MsalBroadcastService,
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.environmentInstanceName !== 'LOCALDEV',
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
};
